import type { DragEvent } from 'react';

/**
 * Small helpers for HTML5 drag-and-drop reordering of vertical lists.
 * Each draggable kind uses its own MIME type so drops only land where they fit
 * (and so the browser's default link/text drags are ignored).
 */

export const DND_MIME = {
  channel: 'application/x-clanfire-channel',
  category: 'application/x-clanfire-category',
  server: 'application/x-clanfire-server',
} as const;
export type DragKind = keyof typeof DND_MIME;

export function dragKindOf(e: DragEvent): DragKind | null {
  const types = e.dataTransfer.types;
  for (const [kind, mime] of Object.entries(DND_MIME) as Array<[DragKind, string]>) if (types.includes(mime)) return kind;
  return null;
}

export function startDrag(e: DragEvent, kind: DragKind, id: string) {
  e.dataTransfer.setData(DND_MIME[kind], id);
  e.dataTransfer.effectAllowed = 'move';
}

/** Whether the pointer is in the lower (or, across a row, right) half of the element: drop after it. */
export function isAfter(e: DragEvent, axis: 'x' | 'y' = 'y'): boolean {
  const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
  return axis === 'x' ? e.clientX > rect.left + rect.width / 2 : e.clientY > rect.top + rect.height / 2;
}

/** Moves `dragId` to just before/after `targetId`. */
export function reorderIds(ids: string[], dragId: string, targetId: string, after: boolean): string[] {
  const rest = ids.filter((id) => id !== dragId);
  const index = rest.indexOf(targetId);
  if (index === -1) return ids;
  rest.splice(index + (after ? 1 : 0), 0, dragId);
  return rest;
}
